/**
 * grading.js — Settle PENDING bets against final scores.
 *
 * Results are PLAIN DATA, shaped like what the site's scores feeds
 * already produce per finished event:
 *
 *   { event: 'Kansas City Chiefs at Denver Broncos',
 *     homeTeam: 'Denver Broncos', awayTeam: 'Kansas City Chiefs',
 *     homeScore: 24, awayScore: 31, final: true }
 *
 * (`event` is optional when homeTeam/awayTeam are present; matching
 * also accepts a result whose `event` equals the bet's event.)
 * This module does no fetching — production passes in results from
 * the same Odds API / ESPN scores feeds the sport pages already use.
 *
 * Outcomes:
 *   'won' | 'lost' | 'push' — graded with the engine's margin rules
 *     (see model.js: margin > 0 won, < 0 lost, 0 push, always from the
 *     bettor's perspective, and stored on the settled bet so the
 *     closest-win/loss stats light up).
 *   'needs_review' — the honest bucket: props and futures (no score
 *     can settle them), parlays with an ungradeable or push leg, bets
 *     whose backed side can't be identified. We NEVER guess a result;
 *     a human (or the book, via sync) settles those.
 */

import { decisionMargin } from '../engine/model.js';

function grNorm(s) {
  return String(s ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * Find the result for a bet (or a parlay leg): exact event-string
 * match first, then "the bet's event names both teams in the result".
 */
export function findResultForBet(betLike, results) {
  if (!Array.isArray(results)) return null;
  const eventNorm = grNorm(betLike.event);
  for (const r of results) {
    if (!r || r.final === false) continue;
    if (eventNorm && grNorm(r.event) === eventNorm) return r;
  }
  for (const r of results) {
    if (!r || r.final === false) continue;
    const home = grNorm(r.homeTeam);
    const away = grNorm(r.awayTeam);
    if (home && away && eventNorm.includes(home) && eventNorm.includes(away)) return r;
  }
  return null;
}

/**
 * Names a selection might use for a team: the full name plus short
 * forms, because hub selections conventionally use short names
 * ("Chiefs -3.5", "Lakers ML") while results carry full names
 * ("Kansas City Chiefs").
 */
function grTeamNameCandidates(fullName) {
  const name = grNorm(fullName);
  if (!name) return [];
  const words = name.split(' ');
  const out = [name, words[words.length - 1]];
  if (words.length > 2) out.push(words.slice(-2).join(' '));
  return [...new Set(out)];
}

/** Which side of the result does the selection back? 'home'|'away'|null. */
function grBackedSide(betLike, result) {
  const sel = grNorm(betLike.selection);
  const inHome = grTeamNameCandidates(result.homeTeam).some((n) => sel.includes(n));
  const inAway = grTeamNameCandidates(result.awayTeam).some((n) => sel.includes(n));
  if (inHome && !inAway) return 'home';
  if (inAway && !inHome) return 'away';
  return null;
}

function grOutcomeFromMargin(margin) {
  if (margin > 0) return 'won';
  if (margin < 0) return 'lost';
  return 'push';
}

const GR_REVIEW = (reason) => ({ outcome: 'needs_review', margin: null, reason });

/**
 * Grade ONE bet.
 *
 * `resultOrResults` is the bet's result object — except for parlays,
 * where it should be the full results array (legs can span events);
 * a single result is accepted when every leg is on that one event.
 *
 * Returns { outcome, margin, reason?, legs? } — for parlays, `legs`
 * carries the graded legs (margins filled in) when grading succeeded.
 * Pure: the input bet is not mutated.
 */
export function gradeBet(bet, resultOrResults) {
  if (!bet || typeof bet !== 'object') return GR_REVIEW('not a bet record');
  if (bet.status !== 'pending') return GR_REVIEW('bet is not pending');

  if (bet.betType === 'parlay') return grGradeParlay(bet, resultOrResults);
  if (bet.betType === 'prop' || bet.betType === 'future') {
    return GR_REVIEW(bet.betType + ' bets cannot be settled from a final score — the book (or the user) settles these');
  }

  const result = Array.isArray(resultOrResults)
    ? findResultForBet(bet, resultOrResults)
    : resultOrResults;
  if (!result) return GR_REVIEW('no final result found for this event');
  if (typeof result.homeScore !== 'number' || typeof result.awayScore !== 'number') {
    return GR_REVIEW('result is missing final scores');
  }

  if (bet.betType === 'moneyline' || bet.betType === 'spread') {
    const side = grBackedSide(bet, result);
    if (!side) return GR_REVIEW('could not tell which team the selection backs');
    const backed = side === 'home' ? result.homeScore : result.awayScore;
    const other = side === 'home' ? result.awayScore : result.homeScore;
    const diff = backed - other;
    if (bet.betType === 'moneyline') {
      return { outcome: grOutcomeFromMargin(diff), margin: diff };
    }
    if (typeof bet.line !== 'number') return GR_REVIEW('spread bet has no line recorded');
    // Model convention: margin = final margin for the backed side + line
    // (Chiefs -3.5 winning by 4 -> 4 + (-3.5) = +0.5).
    const margin = diff + bet.line;
    return { outcome: grOutcomeFromMargin(margin), margin };
  }

  if (bet.betType === 'total') {
    if (typeof bet.line !== 'number') return GR_REVIEW('total bet has no line recorded');
    const sel = grNorm(bet.selection);
    const isOver = /\bover\b/.test(sel);
    const isUnder = /\bunder\b/.test(sel);
    if (isOver === isUnder) return GR_REVIEW('could not tell if the total is over or under');
    const combined = result.homeScore + result.awayScore;
    const margin = isOver ? combined - bet.line : bet.line - combined;
    return { outcome: grOutcomeFromMargin(margin), margin };
  }

  return GR_REVIEW('unsupported bet type for grading: ' + bet.betType);
}

function grGradeParlay(bet, resultOrResults) {
  const results = Array.isArray(resultOrResults) ? resultOrResults : null;
  if (!Array.isArray(bet.legs) || bet.legs.length === 0) {
    return GR_REVIEW('parlay has no legs recorded');
  }
  const gradedLegs = [];
  let sawLost = false;
  for (const leg of bet.legs) {
    const legBet = {
      ...bet,
      betType: leg.betType,
      selection: leg.selection,
      line: leg.line ?? null,
      event: leg.event,
      status: 'pending',
      legs: undefined,
    };
    const legResult = results ? findResultForBet(legBet, results) : resultOrResults;
    if (!legResult) return GR_REVIEW('no final result for leg: ' + (leg.selection || leg.event || '?'));
    const legGrade = gradeBet(legBet, legResult);
    if (legGrade.outcome === 'needs_review') {
      return GR_REVIEW('leg needs review (' + (leg.selection || leg.event || '?') + '): ' + legGrade.reason);
    }
    if (legGrade.outcome === 'push') {
      // Books reduce a parlay around a push leg and reprice the whole
      // ticket — the hub cannot reproduce that price, so a human (or
      // the book, via sync) settles it. Never guess the payout.
      return GR_REVIEW('a leg pushed — books reduce parlays around push legs; needs the book value');
    }
    if (legGrade.outcome === 'lost') sawLost = true;
    gradedLegs.push({ ...leg, margin: legGrade.margin });
  }
  const outcome = sawLost ? 'lost' : 'won';
  // Reuse the engine's parlay margin rule on the graded legs.
  const margin = decisionMargin({ ...bet, status: outcome, legs: gradedLegs, margin: null });
  return { outcome, margin, legs: gradedLegs };
}

/**
 * Grade every pending bet in a list against a results array.
 * Returns:
 *   { bets,            // new array; graded bets are settled copies
 *     graded,          // [{ betId, outcome, margin }]
 *     needsReview,     // [{ betId, reason }] — left pending
 *     ungraded }       // [betId] — no result available, left pending
 * `settledAt` is injectable for tests; defaults to now.
 */
export function gradePending(bets, results, settledAt = new Date().toISOString()) {
  const graded = [];
  const needsReview = [];
  const ungraded = [];
  const out = (bets ?? []).map((bet) => {
    if (bet.status !== 'pending') return bet;
    if (bet.betType !== 'parlay' && !findResultForBet(bet, results)) {
      ungraded.push(bet.id);
      return bet;
    }
    const res = bet.betType === 'parlay' ? gradeBet(bet, results) : gradeBet(bet, findResultForBet(bet, results));
    if (res.outcome === 'needs_review') {
      needsReview.push({ betId: bet.id, reason: res.reason });
      return bet;
    }
    graded.push({ betId: bet.id, outcome: res.outcome, margin: res.margin });
    return {
      ...bet,
      status: res.outcome,
      margin: res.margin,
      ...(res.legs ? { legs: res.legs } : {}),
      settledAt,
    };
  });
  return { bets: out, graded, needsReview, ungraded };
}
